import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Percent, RefreshCw, TrendingUp, FileSpreadsheet, Calendar } from "lucide-react";
import { toast } from "sonner";

/** v82 — Komisi Teknisi (Date Range Dashboard).
 *  Rentang bebas (default siklus akunting: 26 bulan lalu → 25 bulan ini).
 *  Baris = tanggal, kolom = personel, sel = komisi harian.
 *  Rumus: sum(Jasa Teknisi armada X pada tanggal T dimana personel di-assign)
 *         × (personel.commission_percentage / 100)
 */
const rp = (n) => "Rp " + (Number(n) || 0).toLocaleString("id-ID");
// v85.53 — WAJIB pakai LOCAL date formatting; `toISOString()` konversi ke UTC
// yang menggeser tanggal 1 hari di WITA (UTC+8). Default siklus akunting
// 26→25 jadi muncul 25→24.
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
// Default: siklus akunting 26 bln lalu → 25 bln ini.
// Jika hari ini <= 25, siklus = 26 bulan lalu → 25 bulan ini.
// Jika hari ini >= 26, siklus = 26 bulan ini → 25 bulan depan.
function defaultCycle() {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();      // 0-based
  const d = now.getDate();
  let from, to;
  if (d <= 25) {
    from = new Date(y, m - 1, 26);
    to   = new Date(y, m, 25);
  } else {
    from = new Date(y, m, 26);
    to   = new Date(y, m + 1, 25);
  }
  return { from: iso(from), to: iso(to) };
}

export default function KomisiTeknisi() {
  const _init = defaultCycle();
  const [dateFrom, setDateFrom] = useState(_init.from);
  const [dateTo, setDateTo]     = useState(_init.to);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  async function load() {
    if (!dateFrom || !dateTo) return;
    if (dateFrom > dateTo) {
      toast.error("Tanggal 'Dari' harus <= 'Sampai'");
      return;
    }
    setLoading(true);
    try {
      setData(await api.commissionDashboard({ date_from: dateFrom, date_to: dateTo }));
    } catch (e) {
      toast.error("Gagal muat: " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateFrom, dateTo]);

  // Preset shortcuts
  function presetThisMonth() {
    const n = new Date();
    setDateFrom(iso(new Date(n.getFullYear(), n.getMonth(), 1)));
    setDateTo(iso(new Date(n.getFullYear(), n.getMonth() + 1, 0)));
  }
  function presetCycle() {
    const c = defaultCycle();
    setDateFrom(c.from);
    setDateTo(c.to);
  }
  function presetLast30() {
    const to = new Date();
    const from = new Date(Date.now() - 29 * 86400000);
    setDateFrom(iso(from));
    setDateTo(iso(to));
  }

  const visibleDates = useMemo(() => {
    if (!data?.dates) return [];
    // v84.146 — Tampilkan tanggal yg punya komisi ATAU kasbon
    const kasbonDates = data.kasbon_cells || {};
    return data.dates.filter(d =>
      (data.totals_per_date?.[d] || 0) > 0
      || Object.values(kasbonDates[d] || {}).some(v => (v || 0) > 0)
    );
  }, [data]);

  const personnel = data?.personnel || [];
  const cells = data?.cells || {};
  const kasbonCells = data?.kasbon_cells || {};   // v84.146

  function exportCsv() {
    if (!data?.personnel?.length) { toast.warning("Tidak ada data"); return; }
    const header = ["Tanggal", ...personnel.map(p => `${p.name} (${p.commission_percentage}%)`), "Total"];
    const rows = visibleDates.map(d => {
      const cellRow = personnel.map(p => cells[d]?.[p.id]?.amount || 0);
      const total = data.totals_per_date?.[d] || 0;
      return [d, ...cellRow, total];
    });
    const totalRow = ["TOTAL", ...personnel.map(p => data.totals_per_personnel?.[p.id] || 0), data.grand_total || 0];
    const csv = [header, ...rows, totalRow].map(r =>
      r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(",")
    ).join("\n");
    const url = URL.createObjectURL(new Blob([csv], {type:"text/csv"}));
    const a = document.createElement("a");
    a.href = url; a.download = `komisi-teknisi-${dateFrom}_${dateTo}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-4" data-testid="komisi-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold tracking-tight flex items-center gap-2">
            <Percent className="w-5 h-5 text-emerald-700" />
            Komisi Teknisi
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Rekap komisi per rentang tanggal. Default siklus akunting <b>26 bulan lalu → 25 bulan ini</b>. Diperoleh dari <b>Jasa Teknisi armada × % komisi</b>. Kelola master data di <b>Data Teknisi</b>, assign personel ke armada di <b>Rekap Penagihan</b>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!data?.personnel?.length}
            className="h-9 rounded-sm gap-1.5" data-testid="komisi-export-csv-btn">
            <FileSpreadsheet className="w-3.5 h-3.5" /> Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}
            className="h-9 rounded-sm gap-1.5" data-testid="komisi-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${loading?"animate-spin":""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Filter rentang tanggal */}
      <div className="border border-zinc-200 rounded-sm bg-white p-3">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">
          <Calendar className="w-3 h-3"/> Rentang Tanggal
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
          <div className="md:col-span-1">
            <label className="text-[10px] text-zinc-500 block mb-1">Dari Tanggal</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="rounded-sm h-9 text-[12px] font-mono"
              data-testid="komisi-date-from-input" />
          </div>
          <div className="md:col-span-1">
            <label className="text-[10px] text-zinc-500 block mb-1">Sampai Tanggal</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="rounded-sm h-9 text-[12px] font-mono"
              data-testid="komisi-date-to-input" />
          </div>
          <div className="md:col-span-2 flex gap-1">
            <Button variant="outline" size="sm" onClick={presetCycle}
              className="h-9 rounded-sm text-[11px] flex-1"
              data-testid="preset-cycle-btn"
              title="26 bln lalu → 25 bln ini (siklus akunting)">
              26 → 25
            </Button>
            <Button variant="outline" size="sm" onClick={presetThisMonth}
              className="h-9 rounded-sm text-[11px] flex-1"
              data-testid="preset-month-btn"
              title="1 → akhir bulan ini">
              Bulan Ini
            </Button>
            <Button variant="outline" size="sm" onClick={presetLast30}
              className="h-9 rounded-sm text-[11px] flex-1"
              data-testid="preset-30d-btn"
              title="30 hari terakhir">
              30 Hari
            </Button>
          </div>
          <div className="md:col-span-2 border-2 border-emerald-700 bg-emerald-50 rounded-sm p-3">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">
              <TrendingUp className="w-3.5 h-3.5" /> Total Komisi Rentang Ini
            </div>
            <div className="text-2xl font-bold text-emerald-700 mt-0.5 font-mono" data-testid="komisi-grand-total">
              {rp(data?.grand_total || 0)}
            </div>
          </div>
        </div>
      </div>

      {/* Matrix table — v85.52 freeze pane: scroll bebas vertikal + horizontal,
          header baris (nama teknisi) & kolom (tanggal) tetap terlihat. */}
      <div className="border border-zinc-200 rounded-sm bg-white overflow-auto max-h-[70vh]">
        {loading ? (
          <div className="p-12 text-center text-zinc-400">
            <RefreshCw className="w-6 h-6 animate-spin inline mr-2" /> Memuat…
          </div>
        ) : personnel.length === 0 ? (
          <div className="p-12 text-center text-zinc-400">
            Belum ada personel. Buka menu <b>Data Teknisi</b> untuk menambahkan personel + % komisi.
          </div>
        ) : (
          <table className="w-full border-collapse text-[13px]">
            <thead className="bg-zinc-50 sticky top-0 z-20">
              <tr>
                <th className="w-28 sticky left-0 bg-zinc-50 z-30 text-left px-3 py-2 font-semibold text-zinc-700 border-r border-b border-zinc-200">Tanggal</th>
                {personnel.map(p => (
                  <th key={p.id} className="text-right min-w-[120px] px-3 py-2 border-b border-zinc-200" data-testid={`komisi-col-${p.id}`}>
                    <div className="font-semibold text-zinc-800">{p.name}</div>
                    <div className="text-[10px] text-emerald-700 font-mono font-normal">{p.commission_percentage}%</div>
                  </th>
                ))}
                <th className="text-right min-w-[120px] bg-orange-50 text-[#E65100] px-3 py-2 font-semibold border-b border-zinc-200">Total/Hari</th>
              </tr>
            </thead>
            <tbody>
              {visibleDates.length === 0 && (
                <tr>
                  <td colSpan={personnel.length + 2} className="text-center text-zinc-400 py-8">
                    Belum ada transaksi/assignment pada rentang {dateFrom} → {dateTo}.
                  </td>
                </tr>
              )}
              {visibleDates.map(d => (
                <tr key={d} data-testid={`komisi-row-${d}`} className="hover:bg-zinc-50/60">
                  <td className="font-mono text-[11px] sticky left-0 bg-white z-10 border-r border-b border-zinc-200 px-3 py-1.5">{d}</td>
                  {personnel.map(p => {
                    const cell = cells[d]?.[p.id];
                    const amt = cell?.amount || 0;
                    const sources = cell?.sources || [];
                    const kasbon = kasbonCells[d]?.[p.id] || 0;
                    return (
                      <td key={p.id} className="text-right font-mono text-[12px] border-b border-zinc-100 px-3 py-1.5"
                        data-testid={`komisi-cell-${d}-${p.id}`}>
                        {amt > 0 ? (
                          <span
                            className="text-emerald-700 font-semibold cursor-help"
                            title={sources.map(s => `${s.armada_name}: ${rp(s.jasa_teknisi)} × ${p.commission_percentage}% = ${rp(s.commission)}`).join("\n")}
                          >
                            {rp(amt)}
                          </span>
                        ) : (
                          <span className="text-zinc-300">—</span>
                        )}
                        {kasbon > 0 && (
                          <div className="text-[10px] font-semibold text-orange-500 mt-0.5"
                            data-testid={`komisi-kasbon-${d}-${p.id}`}
                            title={`Kasbon (dari Rekap Kerja Teknisi): ${rp(kasbon)}`}>
                            {rp(kasbon)}
                          </div>
                        )}
                      </td>
                    );
                  })}
                  <td className="text-right font-mono text-[12px] font-semibold text-[#E65100] bg-orange-50/50 border-b border-zinc-100 px-3 py-1.5">
                    {rp(data?.totals_per_date?.[d] || 0)}
                  </td>
                </tr>
              ))}
              {visibleDates.length > 0 && (
                <tr className="bg-emerald-50/60 font-semibold sticky bottom-0 z-20">
                  <td className="text-[11px] uppercase tracking-widest text-emerald-800 sticky left-0 bg-emerald-50/60 z-30 border-r border-t border-zinc-200 px-3 py-2">
                    TOTAL
                  </td>
                  {personnel.map(p => (
                    <td key={p.id} className="text-right font-mono text-[13px] text-emerald-700 border-t border-zinc-200 px-3 py-2"
                      data-testid={`komisi-total-${p.id}`}>
                      {rp(data?.totals_per_personnel?.[p.id] || 0)}
                    </td>
                  ))}
                  <td className="text-right font-mono text-[13px] text-[#E65100] bg-orange-100 border-t border-zinc-200 px-3 py-2"
                    data-testid="komisi-grand-total-row">
                    {rp(data?.grand_total || 0)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="text-[11px] text-zinc-500 border-l-2 border-emerald-300 pl-3 py-1">
        💡 Hover angka komisi untuk lihat detail per armada. Nilai <span className="text-orange-500 font-semibold">Kasbon</span> harian diambil dari halaman <b>Rekap Kerja Teknisi</b>. Tanggal tanpa komisi maupun kasbon disembunyikan otomatis. Preset <b>26 → 25</b> = siklus akunting bulanan.
      </div>
    </div>
  );
}
